import {
  Anchor,
  AppShell,
  Burger,
  Container,
  Group,
  Image,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { getCurrentUser } from "../shared/getCurrentUser";
import { imgLogoNoText128 } from "../shared/images";
import { OfflineNotification } from "./OfflineNotification";

type Props = {
  title?: string;
  children: React.ReactNode;
  /** marketing = public site chrome; app = distributor portal chrome */
  variant?: "marketing" | "app";
};

const navLinkProps = {
  c: "white" as const,
  underline: "never" as const,
  fw: 500,
};

export function NavBarContainer({
  title,
  children,
  variant = "app",
}: Props) {
  const { t } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure(false);
  const currentUser = getCurrentUser();
  const isMarketing = variant === "marketing";

  const brandTo = isMarketing ? "/" : "/distributorportal";

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 260,
        breakpoint: "sm",
        collapsed: { desktop: true, mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header bg="brand.6">
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              color="white"
            />
            <Anchor component={Link} to={brandTo} {...navLinkProps}>
              <Text span fw={700} c="white">
                Din
              </Text>
              <Text span fw={700} c="gray.1">
                Label
              </Text>
              {!isMarketing && (
                <Text span c="white">
                  {" "}
                  {t("nav.distributors")}
                </Text>
              )}
            </Anchor>
            <Anchor component={Link} to={brandTo}>
              <Image src={imgLogoNoText128} alt={t("nav.logoAlt")} w={40} h={40} />
            </Anchor>
          </Group>

          <Group gap="md" visibleFrom="sm" wrap="nowrap">
            {isMarketing ? (
              <>
                <Anchor component={Link} to="/install" {...navLinkProps}>
                  {t("nav.download")}
                </Anchor>
                <Anchor component={Link} to="/buy" {...navLinkProps}>
                  {t("nav.buy")}
                </Anchor>
                <Anchor component={Link} to="/tutorials" {...navLinkProps}>
                  {t("nav.tutorials")}
                </Anchor>
                {currentUser ? (
                  <>
                    <Anchor component={Link} to="/userinfo" {...navLinkProps}>
                      {t("nav.myAccount")}
                    </Anchor>
                    <Anchor href="/logout" {...navLinkProps}>
                      {t("nav.logout")}
                    </Anchor>
                  </>
                ) : (
                  <Anchor component={Link} to="/signin" {...navLinkProps}>
                    {t("nav.login")}
                  </Anchor>
                )}
              </>
            ) : (
              <>
                <Anchor component={Link} to="/distributorportal" {...navLinkProps}>
                  {t("nav.home")}
                </Anchor>
                <Anchor component={Link} to="/faq" {...navLinkProps}>
                  {t("nav.faq")}
                </Anchor>
                <Anchor component={Link} to="/tutoriales" {...navLinkProps}>
                  {t("nav.tutorials")}
                </Anchor>
                <Anchor href="mailto:support@dinlabel.com" {...navLinkProps}>
                  {t("nav.contact")}
                </Anchor>
                {currentUser && (
                  <Anchor href="/logout" {...navLinkProps}>
                    {t("nav.logout")}
                  </Anchor>
                )}
              </>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        {isMarketing ? (
          <>
            <Anchor component={Link} to="/install" onClick={close} py="xs">
              {t("nav.download")}
            </Anchor>
            <Anchor component={Link} to="/buy" onClick={close} py="xs">
              {t("nav.buy")}
            </Anchor>
            <Anchor component={Link} to="/tutorials" onClick={close} py="xs">
              {t("nav.tutorials")}
            </Anchor>
            {currentUser ? (
              <>
                <Anchor component={Link} to="/userinfo" onClick={close} py="xs">
                  {t("nav.myAccount")}
                </Anchor>
                <Anchor href="/logout" onClick={close} py="xs">
                  {t("nav.logout")}
                </Anchor>
              </>
            ) : (
              <Anchor component={Link} to="/signin" onClick={close} py="xs">
                {t("nav.login")}
              </Anchor>
            )}
          </>
        ) : (
          <>
            <Anchor component={Link} to="/distributorportal" onClick={close} py="xs">
              {t("nav.home")}
            </Anchor>
            <Anchor component={Link} to="/faq" onClick={close} py="xs">
              {t("nav.faq")}
            </Anchor>
            <Anchor component={Link} to="/tutoriales" onClick={close} py="xs">
              {t("nav.tutorials")}
            </Anchor>
            <Anchor href="mailto:support@dinlabel.com" onClick={close} py="xs">
              {t("nav.contact")}
            </Anchor>
            {currentUser && (
              <Anchor href="/logout" onClick={close} py="xs">
                {t("nav.logout")}
              </Anchor>
            )}
          </>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        <Container size="lg">
          <OfflineNotification />
          {title && (
            <Title order={2} mb="md">
              {title}
            </Title>
          )}
          {children}
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
